import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { isObject } from "lodash";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { UploadImageCard } from "src/_modules/common";
import { getImageURL } from "src/_utils";
import { MapitPhoneInput, MapItTextField } from "src/components/common";
import { ROLES } from "src/constants";

export default function ProfileBusinessInfoCard({
  commercialProfileData,
  onChange,
  validationErrors,
  onUpateProfile,
  loading,
  isSuccess,
  error,
  isDialog = false,
  updateDataInMechant,
  rejectionReasons,
}) {
 
  const { t } = useTranslation();
  const { profile } = useSelector((state) => state.entities.profile);

  const isCompany = commercialProfileData?.isCompany === true;

  const lastReason = rejectionReasons?.at(-1) || null;

  const isAdmin = profile.role === ROLES.admin;

  const showUpdateButton =
    isDialog || isAdmin || (!isAdmin && updateDataInMechant === false);
  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        <CardHeader title={t("Business Info")} />
        <Divider />
      </Box>

      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid item xs={12} md={6}>
              <FormControl error={!!validationErrors?.isCompany}>
                <FormLabel>{t("Account Type")}</FormLabel>

                <RadioGroup
                  row
                  name="isCompany"
                  value={
                    commercialProfileData?.isCompany === true
                      ? "company"
                      : "individual"
                  }
                  onChange={(e) => {
                    onChange({
                      target: {
                        name: "isCompany",
                        value: e.target.value === "company",
                      },
                    });
                  }}
                >
                  <FormControlLabel
                    value="individual"
                    control={<Radio />}
                    label={t("Individual")}
                  />
                  <FormControlLabel
                    value="company"
                    control={<Radio />}
                    label={t("Company")}
                  />
                </RadioGroup>

                {validationErrors?.isCompany && (
                  <FormHelperText>{validationErrors.isCompany}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Collapse in={isCompany} timeout={300}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <UploadImageCard
                    title={t("Commercial Register")}
                    name="commercialRegister"
                    accept=".jpg,.jpeg,.png,.gif,.bmp,.pdf"
                    image={
                      isObject(commercialProfileData?.commercialRegister)
                        ? getImageURL({
                            image: commercialProfileData?.commercialRegister,
                          })
                        : commercialProfileData?.commercialRegister
                    }
                    handleChange={onChange}
                    error={validationErrors?.commercialRegister ?? ""}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <UploadImageCard
                    title={t("National Address")}
                    name="nationalAddress"
                    accept=".jpg,.jpeg,.png,.gif,.bmp,.pdf"
                    image={
                      isObject(commercialProfileData?.nationalAddress)
                        ? getImageURL({
                            image: commercialProfileData?.nationalAddress,
                          })
                        : commercialProfileData?.nationalAddress
                    }
                    handleChange={onChange}
                    error={validationErrors?.nationalAddress ?? ""}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <UploadImageCard
                    title={t("VAT License")}
                    name="vatLicense"
                    accept=".jpg,.jpeg,.png,.gif,.bmp,.pdf"
                    image={
                      isObject(commercialProfileData?.vatLicense)
                        ? getImageURL({
                            image: commercialProfileData?.vatLicense,
                          })
                        : commercialProfileData?.vatLicense
                    }
                    handleChange={onChange}
                    error={validationErrors?.vatLicense ?? ""}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>

          {/* ===== Inputs Section ===== */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={isCompany ? 6 : 12}>
                <MapItTextField
                  label={t("Name")}
                  size="small"
                  name="name"
                  value={commercialProfileData?.name ?? ""}
                  required
                  handleChange={onChange}
                  error={validationErrors?.name ?? ""}
                />
              </Grid>

              <Grid item xs={12} md={isCompany ? 6 : 12}>
                <MapItTextField
                  label={t("Email")}
                  size="small"
                  name="email"
                  value={commercialProfileData?.email ?? ""}
                  required
                  handleChange={onChange}
                  error={validationErrors?.email ?? ""}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={isCompany ? 6 : 12}
                mb={isCompany ? 2 : ""}
              >
                <MapitPhoneInput
                  label={t("Phone")}
                  size="small"
                  name="phone"
                  value={commercialProfileData?.phone ?? ""}
                  handleChange={onChange}
                  error={validationErrors?.phone ?? ""}
                />
              </Grid>
            </Grid>
            <Collapse in={isCompany} timeout={300}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <MapItTextField
                    label={t("Commercial Register Number")}
                    size="small"
                    name="commercialRegisterNumber"
                    value={
                      commercialProfileData?.commercialRegisterNumber ?? ""
                    }
                    required
                    handleChange={onChange}
                    error={validationErrors?.commercialRegisterNumber ?? ""}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <MapItTextField
                    label={t("Tax Number")}
                    size="small"
                    name="taxNumber"
                    value={commercialProfileData?.taxNumber ?? ""}
                    required
                    handleChange={onChange}
                    error={validationErrors?.taxNumber ?? ""}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <MapItTextField
                    label={t("City")}
                    size="small"
                    name="city"
                    value={commercialProfileData?.city ?? ""}
                    required
                    handleChange={onChange}
                    error={validationErrors?.city ?? ""}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <MapItTextField
                    label={t("Street")}
                    size="small"
                    name="street"
                    value={commercialProfileData?.street ?? ""}
                    required
                    handleChange={onChange}
                    error={validationErrors?.street ?? ""}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <MapItTextField
                    label={t("Building Number")}
                    size="small"
                    name="buildingNumber"
                    value={commercialProfileData?.buildingNumber ?? ""}
                    required
                    handleChange={onChange}
                    error={validationErrors?.buildingNumber ?? ""}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <MapItTextField
                    label={t("District")}
                    size="small"
                    name="district"
                    value={commercialProfileData?.district ?? ""}
                    required
                    handleChange={onChange}
                    error={validationErrors?.district ?? ""}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <MapItTextField
                    label={t("Zip Code")}
                    size="small"
                    name="zipCode"
                    value={commercialProfileData?.zipCode ?? ""}
                    handleChange={onChange}
                    error={validationErrors?.zipCode ?? ""}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </Grid>

        {lastReason && (
          <Alert
            sx={{ width: "100%", mt: 2 }}
            variant="outlined"
            severity="error"
          >
            {lastReason}
          </Alert>
        )}
      </CardContent>

      <Box>
        <Divider />
        <CardActions
          sx={{
            display: "flex",
            justifyContent: error || isSuccess ? "space-between" : "flex-end",
            alignItems: "center",
          }}
        >
          {error && <Typography color="error">{t(error)}</Typography>}
          {isSuccess && (
            <Typography color="success.main">
              {t("Please be informed that your data will be reviewed by the responsible party, and you will be notified once it is approved.")}
            </Typography>
          )}

          {showUpdateButton && (
            <LoadingButton
              variant="contained"
              loading={loading}
              onClick={onUpateProfile}
            >
              {t("Update")}
            </LoadingButton>
          )}
        </CardActions>
      </Box>
    </Card>
  );
}
